import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/help/what-is-agentframer")({
	component: WhatIsAgentFramer,
});

function InlineCode({ children }: { children: React.ReactNode }) {
	return (
		<code
			style={{
				color: "#6B3FF2",
				fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
				fontSize: "0.82em",
				backgroundColor: "rgba(234,40,4,0.08)",
				border: "1px solid rgba(234,40,4,0.15)",
				borderLeft: "2px solid #6B3FF2",
				padding: "1px 6px",
				fontWeight: "bold",
			}}
		>
			{children}
		</code>
	);
}

export function WhatIsAgentFramer() {
	return (
		<article className="space-y-12">
			{/* Header */}
			<div className="space-y-3">
				<h1
					className="text-4xl font-semibold tracking-tight"
					style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
				>
					What is AgentFramer?
				</h1>
				<p
					className="text-base font-bold"
					style={{ color: "var(--km-on-surface-muted)" }}
				>
					The MCP-native media generation layer for agentic workflows.
				</p>
			</div>

			{/* Lead paragraph */}
			<div
				className="p-5 text-sm leading-relaxed"
				style={{
					color: "var(--km-on-surface-muted)",
					backgroundColor: "rgba(234,40,4,0.04)",
					border: "1px solid rgba(234,40,4,0.2)",
					borderLeft: "3px solid #6B3FF2",
				}}
			>
				AgentFramer is the media generation layer for AI agents. It exposes
				image, video, and audio generation as MCP tools, so any
				MCP-compatible assistant (Claude, Cursor, Windsurf) can generate and
				retrieve media without you writing a single line of provider
				integration code.
			</div>

			{/* Sections */}
			<div className="space-y-10">
				{/* Problem */}
				<section id="the-problem" className="scroll-mt-20 space-y-4">
					<div className="space-y-2">
						<p className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: "#6B3FF2", fontFamily: "inherit" }}>01 / The Problem</p>
						<div className="flex items-center gap-3">
							<span
								className="material-symbols-outlined p-1.5"
								style={{ fontSize: "16px", color: "#6B3FF2", backgroundColor: "rgba(234,40,4,0.1)" }}
								aria-hidden="true"
							>
								error_outline
							</span>
							<h2
								className="text-lg font-semibold tracking-tight"
								style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
							>
								The problem it solves
							</h2>
						</div>
					</div>
					<div className="space-y-3 text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
						<p>
							LLMs can plan, reason, and act. But the moment a workflow needs a
							product image, a voice line, or a video clip, the pipeline stalls.
							You'd have to wire up separate provider APIs, handle async job
							polling, rotate API keys, and build retry logic, for every media
							type.
						</p>
						<p>
							AgentFramer collapses all of that into a single MCP server. Your
							agent calls <InlineCode>generate_image</InlineCode>,{" "}
							<InlineCode>generate_video</InlineCode>, or{" "}
							<InlineCode>generate_audio</InlineCode> and gets back a URL. No
							credentials to manage, no polling boilerplate, no provider accounts.
						</p>
					</div>
				</section>

				{/* Dashboard */}
				<section id="dashboard" className="scroll-mt-20 space-y-4">
					<div className="space-y-2">
						<p className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: "#6B3FF2", fontFamily: "inherit" }}>02 / The Dashboard</p>
						<div className="flex items-center gap-3">
							<span
								className="material-symbols-outlined p-1.5"
								style={{ fontSize: "16px", color: "#6B3FF2", backgroundColor: "rgba(234,40,4,0.1)" }}
								aria-hidden="true"
							>
								dashboard
							</span>
							<h2
								className="text-lg font-semibold tracking-tight"
								style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
							>
								The dashboard is a view layer
							</h2>
						</div>
					</div>
					<div className="space-y-3 text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
						<p>
							The AgentFramer web dashboard at{" "}
							<a
								href="https://agentframer.com"
								style={{ color: "#6B3FF2", textDecoration: "underline dotted" }}
							>
								agentframer.com
							</a>{" "}
							is a view layer. It shows you what your agents have generated, lets
							you manage API keys, top up your balance, and invite teammates. The
							real work happens through the MCP server.
						</p>
						<p>
							The web UI and MCP connect to the same backend. Every generation
							your agent triggers through MCP shows up in the dashboard. Use
							whichever fits your workflow, they're two windows into the same
							system.
						</p>
					</div>
				</section>

				{/* Available today */}
				<section id="available-today" className="scroll-mt-20 space-y-4">
					<div className="space-y-2">
						<p className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: "#2b9a66", fontFamily: "inherit" }}>03 / Available Now</p>
						<div className="flex items-center gap-3">
							<span
								className="material-symbols-outlined p-1.5"
								style={{ fontSize: "16px", color: "#2b9a66", backgroundColor: "rgba(43,154,102,0.1)" }}
								aria-hidden="true"
							>
								check_circle
							</span>
							<h2
								className="text-lg font-semibold tracking-tight"
								style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
							>
								What's available today
							</h2>
						</div>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
						{[
							"Image generation (Nano Banana 2, Recraft, GPT Image 2)",
							"Video synthesis (Seedance, Kling, HappyHorse, Grok, HeyGen)",
							"Audio generation (Eleven Music v1)",
							"Generation history",
							"Balance management",
							"Workspace switching",
							"Team invitations",
						].map((item) => (
							<div
								key={item}
								className="flex items-start gap-2.5 px-3 py-2.5 text-sm"
								style={{
									border: "1px solid #2b9a66",
									borderLeft: "2px solid #2b9a66",
									backgroundColor: "rgba(43,154,102,0.06)",
									color: "var(--km-on-surface-muted)",
								}}
							>
								<span
									className="material-symbols-outlined shrink-0 mt-0.5"
									style={{ fontSize: "14px", color: "#2b9a66", fontVariationSettings: "'FILL' 1" }}
									aria-hidden="true"
								>
									check
								</span>
								{item}
							</div>
						))}
					</div>
					<p className="text-xs" style={{ color: "var(--km-on-surface-muted)" }}>
						All live, not roadmap items.
					</p>
				</section>
			</div>

			{/* Nav */}
			<div
				className="flex items-center justify-between text-sm pt-4"
				style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
			>
				<Link
					to="/help/quick-start"
					className="flex items-center gap-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{ color: "var(--km-on-surface-muted)", textDecoration: "none" }}
					onMouseEnter={e => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)";
						(e.currentTarget as HTMLElement).style.textDecoration = "underline dotted";
					}}
					onMouseLeave={e => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)";
						(e.currentTarget as HTMLElement).style.textDecoration = "none";
					}}
					onFocus={e => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)";
						(e.currentTarget as HTMLElement).style.textDecoration = "underline dotted";
					}}
					onBlur={e => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)";
						(e.currentTarget as HTMLElement).style.textDecoration = "none";
					}}
				>
					<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>arrow_back</span>
					Quick start
				</Link>
				<Link
					to="/help/mcp"
					className="flex items-center gap-1.5 transition-colors font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{ color: "#6B3FF2", textDecoration: "none" }}
					onMouseEnter={e => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
					onMouseLeave={e => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
					onFocus={e => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
					onBlur={e => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
				>
					MCP overview
					<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>arrow_forward</span>
				</Link>
			</div>
		</article>
	);
}
